//Modal for assigning location for FC01 Locations

import React, { useMemo, useState, useEffect, useContext } from "react";
import { ModalBase } from "./ModalBase";
import { TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import PageContext from "../../components/Common/PageContext";
import { isInboundLocation } from "../../util/locationTypeUtil";
import modalSize from "../../util/modalSizeEnum";

const useStyles = makeStyles((theme) => ({
	typography: {
		marginBottom: "10px",
	},
}));

/*
 Reusable state for pages that allow changing the location bay
 */
export type AssignModalState = {
	/* Set when changing the location bay for a specific sku */
	sku: string;
	/* The location bay currently assigned to the line item(s) */
	currentLocation: string;
	/* The location bay being scanned by the user */
	scannedLocation: string;
	context: "manual" | "scanned";
	isModalOpen: boolean;
};

export type ModalAssignLocationProps = {
	isOpen: boolean;
	/**
	 * Will be invoked when confirming the location change
	 * @param previousLocation
	 * @param updatedLocation
	 * @param sku (optional)
	 */
	onSubmit: (previousLocation: string, updatedLocation: string, sku?: string) => void;
	onCancel: () => void;
	poLocation: string;
	currentLocation: string;
	/**
	 * Passes the value scanned into the Location / Product text field
	 */
	scannedInboundLocation: string;
	/**
	 * Indicates how the location assignment was triggered.
	 * The value can be either "manual", indicating user chose to select a new location,
	 * or "scanned" indicating user scanned a SKU that already has a location assigned that
	 * doesn't match the location bay they are currently assigning stock.
	 * @type {"manual" | "scanned"}
	 */
	context: "manual" | "scanned";
	sku?: string;
};

const ModalAssignLocation: React.FC<ModalAssignLocationProps> = ({
	isOpen,
	onSubmit,
	onCancel,
	poLocation,
	currentLocation,
	scannedInboundLocation,
	context,
	sku,
}) => {
	const classes = useStyles();
	const [location, setLocation] = useState("");
	const [pageContext] = useContext(PageContext);
	const putAwayLocations = pageContext.putAwayLocations;

	useEffect(() => {
		if (scannedInboundLocation) {
			setLocation(scannedInboundLocation);
		}
	}, [scannedInboundLocation]);

	const [buttonText, setButtonText] = useState("Assign Entire SKU to New Location");
	const [secondaryButtonText, setSecondaryButtonText] = useState("Cancel");
	const [title, setTitle] = useState("Change Assigned Location");
	useEffect(() => {
		if (!sku) {
			setButtonText("Assign all SKUs to New Location");
			setTitle("Edit Assigned Location Group");
		} else if (context === "scanned") {
			setTitle("Location already assigned");
			setSecondaryButtonText("Retain Existing Location");
		}
	}, [sku, context]);

	let validatedPoLocation = poLocation;
	if (validatedPoLocation === "7") {
		validatedPoLocation = "007";
	}
	const isValid = useMemo(() => {
		return isInboundLocation(location, validatedPoLocation) || putAwayLocations.has(location);
	}, [location, putAwayLocations, validatedPoLocation]);

	const handleInputChange = (event) => {
		setLocation(event.target.value);
	};

	return (
		<ModalBase
			isOpen={isOpen}
			title={title}
			primaryButtonName={buttonText}
			isPrimaryButtonDisabled={!isValid}
			secondaryButtonName={secondaryButtonText}
			onPrimaryButtonClick={() => onSubmit(currentLocation, location, sku)}
			onSecondaryButtonClick={onCancel}
			onClose={onCancel}
			size={modalSize.FULLSCREEN}
			children={
				<Grid container spacing={3}>
					<Grid size={12}>
						{sku ? (
							<>
								<Typography className={classes.typography} variant="body1">
									{currentLocation ? (
										<>
											<strong>SKU {sku}</strong> has {context === "scanned" ? "already" : ""} been assigned to location{" "}
											<strong>{currentLocation}</strong>.
										</>
									) : (
										<>
											<strong>SKU {sku}</strong> has not been assigned a location.
										</>
									)}
								</Typography>

								<Typography variant="body1">
									{sku && context === "scanned" ? (
										<span>
											Additional units will be assigned to the same location, or you can enter a new location to
											relocate the entire received quantity
										</span>
									) : (
										<span>Enter a new location to reassign the entire quantity.</span>
									)}
								</Typography>
							</>
						) : (
							<Typography variant="body1">
								Entering a new location will reassign
								{currentLocation ? (
									<>
										{" "}
										all SKUs currently assigned to location <strong>{currentLocation}</strong> in this receive.
									</>
								) : (
									<> all SKUs that have not been assigned a location.</>
								)}
							</Typography>
						)}
					</Grid>
					<Grid size={12}>
						<TextField
							variant="outlined"
							placeholder="New location"
							value={location}
							onChange={handleInputChange}
							fullWidth
						/>
					</Grid>
				</Grid>
			}
		/>
	);
};

export default ModalAssignLocation;
